import React, { useState, useEffect } from 'react';
import './AIProductComparison.css';
import { useNavigate } from 'react-router-dom';

const AIProductComparison = ({ products }) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [comparisonHistory, setComparisonHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  
  // ✅ NEW STATES
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(0);
  const [comparisonMode, setComparisonMode] = useState('standard'); // standard, detailed, quick
  const [favoriteProducts, setFavoriteProducts] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  
  const navigate = useNavigate();

  // Load comparison history and favorites from localStorage
  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('comparisonHistory') || '[]');
    const favorites = JSON.parse(localStorage.getItem('favoriteProducts') || '[]');
    setComparisonHistory(history);
    setFavoriteProducts(favorites);
  }, []);

  const handleProductSelect = (product) => {
    if (selectedProducts.find(p => p.id === product.id)) {
      setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
      setComparisonResult(null);
    } else if (selectedProducts.length < 3) {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const generateAIComparison = () => {
    if (selectedProducts.length < 2) return;
    
    const comparison = {
      bestValue: findBestValue(),
      bestQuality: findBestQuality(),
      recommendation: generateRecommendation(),
      insights: generateInsights(),
      scores: selectedProducts.map(product => ({
        product,
        valueScore: calculateValueScore(product),
        popularityScore: calculatePopularityScore(product),
        overallScore: calculateOverallScore(product),
        specifications: generateSpecificationScore(product),
        // ✅ NEW SCORES
        durabilityScore: calculateDurabilityScore(product),
        ecoScore: calculateEcoScore(product),
        warrantyScore: calculateWarrantyScore(product)
      })),
      timestamp: new Date().toISOString(),
      // ✅ NEW FEATURES
      priceComparison: generatePriceComparison(),
      featureComparison: generateFeatureComparison(),
      prosCons: generateProsCons(),
      costOfOwnership: calculateCostOfOwnership(),
      marketTrends: generateMarketTrends()
    };

    setComparisonResult(comparison);
    saveToHistory(comparison);
  };

  const saveToHistory = (comparison) => {
    const history = [...comparisonHistory, {
      id: Date.now(),
      products: selectedProducts.map(p => p.name),
      timestamp: comparison.timestamp,
      recommendation: comparison.recommendation.product.name
    }].slice(-10);
    setComparisonHistory(history);
    localStorage.setItem('comparisonHistory', JSON.stringify(history));
  };

  const findBestValue = () => {
    return selectedProducts.reduce((best, current) => {
      const bestScore = calculateValueScore(best);
      const currentScore = calculateValueScore(current);
      return currentScore > bestScore ? current : best;
    });
  };

  const findBestQuality = () => {
    return selectedProducts.reduce((best, current) => {
      const bestRating = parseFloat(best.rating || 4);
      const currentRating = parseFloat(current.rating || 4);
      return currentRating > bestRating ? current : best;
    });
  };

  const calculateValueScore = (product) => {
    const price = parseFloat(product.price);
    const rating = parseFloat(product.rating || 4);
    const normalizedPrice = 100 / price;
    return (normalizedPrice * 0.6 + rating * 20 * 0.4).toFixed(1);
  };

  const calculatePopularityScore = (product) => {
    const rating = parseFloat(product.rating || 4);
    const baseScore = rating * 20;
    const randomBoost = Math.random() * 10;
    return (baseScore + randomBoost).toFixed(1);
  };

  const calculateOverallScore = (product) => {
    const valueScore = parseFloat(calculateValueScore(product));
    const popularityScore = parseFloat(calculatePopularityScore(product));
    return ((valueScore * 0.5 + popularityScore * 0.5)).toFixed(1);
  };

  const generateSpecificationScore = (product) => {
    const specs = product.specifications || {};
    const specCount = Object.keys(specs).length;
    return Math.min(100, specCount * 10 + Math.random() * 20).toFixed(1);
  };

  // ✅ NEW: Durability Score
  const calculateDurabilityScore = (product) => {
    const baseScore = parseFloat(product.rating || 4) * 15;
    const priceBonus = parseFloat(product.price) > 100 ? 20 : 10;
    return Math.min(100, baseScore + priceBonus + Math.random() * 15).toFixed(1);
  };

  // ✅ NEW: Eco-Friendly Score
  const calculateEcoScore = (product) => {
    const categoryScores = {
      'Furniture': 75,
      'Decor': 80,
      'Electronics': 55,
      'Lighting': 85
    };
    const baseScore = categoryScores[product.category] || 70;
    return (baseScore + Math.random() * 15).toFixed(1);
  };

  // ✅ NEW: Warranty Score
  const calculateWarrantyScore = (product) => {
    const price = parseFloat(product.price);
    if (price > 200) return (90 + Math.random() * 10).toFixed(1);
    if (price > 100) return (75 + Math.random() * 15).toFixed(1);
    return (60 + Math.random() * 20).toFixed(1);
  };

  // ✅ NEW: Price Comparison with Market Analysis
  const generatePriceComparison = () => {
    const prices = selectedProducts.map(p => parseFloat(p.price));
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    
    return selectedProducts.map(product => {
      const price = parseFloat(product.price);
      const percentDiff = ((price - avgPrice) / avgPrice * 100).toFixed(1);
      
      return {
        product: product.name,
        price: price,
        vsAverage: percentDiff,
        status: price < avgPrice ? 'below' : price > avgPrice ? 'above' : 'average',
        savings: price < avgPrice ? (avgPrice - price).toFixed(2) : '0.00'
      };
    });
  };

  // ✅ NEW: Feature Comparison Matrix
  const generateFeatureComparison = () => {
    const allFeatures = new Set();
    selectedProducts.forEach(product => {
      if (product.specifications) {
        Object.keys(product.specifications).forEach(key => allFeatures.add(key));
      }
    });

    return Array.from(allFeatures).map(feature => ({
      feature: feature,
      products: selectedProducts.map(p => ({
        name: p.name,
        value: p.specifications?.[feature] || 'N/A',
        hasFeature: !!p.specifications?.[feature]
      }))
    }));
  };

  // ✅ NEW: Pros and Cons Generator
  const generateProsCons = () => {
    return selectedProducts.map(product => {
      const price = parseFloat(product.price);
      const rating = parseFloat(product.rating || 4);
      
      const pros = [];
      const cons = [];

      // Pros
      if (rating >= 4.5) pros.push('Highly rated by customers');
      if (price < 100) pros.push('Affordable price point');
      if (product.category === 'Furniture') pros.push('Durable construction');
      if (product.specifications && Object.keys(product.specifications).length > 3) {
        pros.push('Feature-rich product');
      }

      // Cons
      if (price > 200) cons.push('Premium pricing');
      if (rating < 4) cons.push('Mixed customer reviews');
      if (!product.specifications || Object.keys(product.specifications).length < 2) {
        cons.push('Limited specifications available');
      }

      return {
        product: product.name,
        pros: pros.length > 0 ? pros : ['Standard quality product'],
        cons: cons.length > 0 ? cons : ['No significant drawbacks']
      };
    });
  };

  // ✅ NEW: Total Cost of Ownership
  const calculateCostOfOwnership = () => {
    return selectedProducts.map(product => {
      const price = parseFloat(product.price);
      const maintenanceCost = (price * 0.05).toFixed(2); // 5% annual maintenance
      const energyCost = product.category === 'Electronics' ? 24 : 0; // $2/month
      const totalYear1 = (price + parseFloat(maintenanceCost) + energyCost).toFixed(2);
      const totalYear3 = (price + parseFloat(maintenanceCost) * 3 + energyCost * 3).toFixed(2);

      return {
        product: product.name,
        initialCost: price.toFixed(2),
        maintenancePerYear: maintenanceCost,
        energyCostPerYear: energyCost.toFixed(2),
        totalYear1: totalYear1,
        totalYear3: totalYear3
      };
    });
  };

  // ✅ NEW: Market Trends Analysis
  const generateMarketTrends = () => {
    const avgPrice = selectedProducts.reduce((sum, p) => sum + parseFloat(p.price), 0) / selectedProducts.length;
    const avgRating = selectedProducts.reduce((sum, p) => sum + parseFloat(p.rating || 4), 0) / selectedProducts.length;

    return {
      marketSegment: avgPrice > 150 ? 'Premium' : avgPrice > 75 ? 'Mid-Range' : 'Budget',
      customerSatisfaction: avgRating >= 4.5 ? 'Excellent' : avgRating >= 4 ? 'Good' : 'Average',
      priceStability: 'Stable',
      recommendation: avgPrice > 150 && avgRating >= 4.5 ? 
        'Premium quality justified by customer satisfaction' :
        avgPrice < 75 && avgRating >= 4 ?
        'Excellent value for budget-conscious buyers' :
        'Balanced market positioning'
    };
  };

  const generateRecommendation = () => {
    const scores = selectedProducts.map(p => ({
      product: p,
      score: parseFloat(calculateOverallScore(p))
    })).sort((a, b) => b.score - a.score);

    const winner = scores[0].product;
    const reasons = [];

    const prices = selectedProducts.map(p => parseFloat(p.price));
    const minPrice = Math.min(...prices);
    
    if (parseFloat(winner.price) === minPrice) {
      reasons.push('most affordable option');
    }

    const ratings = selectedProducts.map(p => parseFloat(p.rating || 4));
    const maxRating = Math.max(...ratings);
    
    if (parseFloat(winner.rating || 4) === maxRating) {
      reasons.push('highest rated');
    }

    if (parseFloat(calculateValueScore(winner)) > 70) {
      reasons.push('best value for money');
    }

    if (winner.specifications && Object.keys(winner.specifications).length > 5) {
      reasons.push('feature-rich');
    }

    return {
      product: winner,
      reasons: reasons.length > 0 ? reasons : ['well-balanced choice']
    };
  };

  const generateInsights = () => {
    const insights = [];
    const prices = selectedProducts.map(p => parseFloat(p.price));
    const maxPrice = Math.max(...prices);
    const minPrice = Math.min(...prices);
    const priceDiff = maxPrice - minPrice;
    const priceDiffPercent = ((priceDiff / minPrice) * 100).toFixed(0);

    insights.push({
      icon: '💰',
      title: 'Price Range',
      description: `${priceDiffPercent}% difference between highest ($${maxPrice}) and lowest ($${minPrice}) priced items`,
      highlight: priceDiffPercent > 50 ? 'high' : priceDiffPercent > 25 ? 'good' : 'excellent'
    });

    const ratings = selectedProducts.map(p => parseFloat(p.rating || 4));
    const avgRating = (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1);

    insights.push({
      icon: '⭐',
      title: 'Average Rating',
      description: `${avgRating}/5.0 stars across selected products`,
      highlight: avgRating >= 4.5 ? 'excellent' : avgRating >= 4 ? 'good' : 'recommended'
    });

    insights.push({
      icon: '🎯',
      title: 'Best Match',
      description: `${findBestValue().name} offers the best value proposition`,
      highlight: 'recommended'
    });

    const bestQuality = findBestQuality();
    insights.push({
      icon: '🏆',
      title: 'Top Rated',
      description: `${bestQuality.name} has the highest customer rating at ${bestQuality.rating || 4}/5`,
      highlight: 'excellent'
    });

    const valueScores = selectedProducts.map(p => parseFloat(calculateValueScore(p)));
    const avgValueScore = (valueScores.reduce((a, b) => a + b, 0) / valueScores.length).toFixed(1);

    insights.push({
      icon: '📊',
      title: 'Value Analysis',
      description: `Average value score is ${avgValueScore}/100 across selections`,
      highlight: avgValueScore >= 75 ? 'excellent' : avgValueScore >= 60 ? 'good' : 'recommended'
    });

    const avgPrice = (prices.reduce((a, b) => a + b, 0) / prices.length).toFixed(2);
    insights.push({
      icon: '💡',
      title: 'Market Position',
      description: `Average price point is $${avgPrice}, indicating ${avgPrice > 100 ? 'premium' : avgPrice > 50 ? 'mid-range' : 'budget-friendly'} segment`,
      highlight: 'recommended'
    });

    return insights;
  };

  // ✅ NEW: Toggle Favorite
  const toggleFavorite = (productId) => {
    const newFavorites = favoriteProducts.includes(productId)
      ? favoriteProducts.filter(id => id !== productId)
      : [...favoriteProducts, productId];
    
    setFavoriteProducts(newFavorites);
    localStorage.setItem('favoriteProducts', JSON.stringify(newFavorites));
  };

  // ✅ NEW: Share Comparison
  const handleShare = () => {
    const shareData = {
      products: selectedProducts.map(p => p.name).join(' vs '),
      winner: comparisonResult?.recommendation?.product?.name,
      timestamp: new Date().toLocaleDateString()
    };

    const shareText = `Check out my product comparison: ${shareData.products}\nRecommended: ${shareData.winner}\nCompared on ${shareData.timestamp}`;

    if (navigator.share) {
      navigator.share({
        title: 'Product Comparison',
        text: shareText,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(shareText);
      alert('Comparison link copied to clipboard!');
    }
  };

  // ✅ NEW: Export to PDF (mock)
  const exportToPDF = () => {
    const exportData = {
      selectedProducts: selectedProducts.map(p => ({ name: p.name, price: p.price, rating: p.rating })),
      comparisonResult: comparisonResult,
      timestamp: new Date().toISOString()
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `comparison-${Date.now()}.json`;
    link.click();
    
    alert('✅ Comparison report exported!');
  };

  // Render 3D Model or Image
  const renderProductMedia = (product, height = '150px') => {
    if (product.modelSrc) {
      return (
        <model-viewer
          src={product.modelSrc}
          ios-src={product.iOSSrc}
          alt={product.name}
          auto-rotate
          camera-controls
          style={{ width: '100%', height: height }}
        />
      );
    }
    return <img src={product.image} alt={product.name} className="product-thumb" />;
  };

  // Filter and sort products
  const getFilteredProducts = () => {
    return products
      .filter(p => {
        const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesPrice = parseFloat(p.price) >= priceRange.min && parseFloat(p.price) <= priceRange.max;
        const matchesRating = parseFloat(p.rating || 4) >= ratingFilter;
        return matchesCategory && matchesSearch && matchesPrice && matchesRating;
      })
      .sort((a, b) => {
        if (sortBy === 'price-low') return parseFloat(a.price) - parseFloat(b.price);
        if (sortBy === 'price-high') return parseFloat(b.price) - parseFloat(a.price);
        if (sortBy === 'rating') return parseFloat(b.rating || 4) - parseFloat(a.rating || 4);
        return a.name.localeCompare(b.name);
      });
  };

  const categories = ['all', ...new Set(products.map(p => p.category))];

  return (
    <div className="ai-comparison-container">
      {/* Header */}
      <div className="comparison-header">
        <h2>
          🤖 AI Product Comparison
          <span className="ai-badge-comp">AI Powered</span>
        </h2>
        <p>Select 2-3 products to compare with AI-powered analysis</p>
      </div>

      {/* Controls */}
      <div className="comparison-controls">
        <div className="search-box">
          <div className="search-icon">🔍</div>
          <div>
            <input
              type="text"
              className="search-input"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="filter-controls">
          <select
            className="filter-select"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            {categories.filter(c => c !== 'all').map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          <select
            className="sort-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Sort by Name</option>
            <option value="price-low">Price: Low to High</option>
            <option value="price-high">Price: High to Low</option>
            <option value="rating">Highest Rated</option>
          </select>

          {/* ✅ NEW: Advanced Filters Toggle */}
          <button
            className="history-btn"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          >
            {showAdvancedFilters ? '🔼' : '🔽'} Filters
          </button>

          <button
            className="history-btn"
            onClick={() => setShowHistory(!showHistory)}
          >
            📜 History ({comparisonHistory.length})
          </button>
        </div>

        {/* ✅ NEW: Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="advanced-filters-panel">
            <div className="filter-group">
              <label>Price Range: ${priceRange.min} - ${priceRange.max}</label>
              <input
                type="range"
                min="0"
                max="1000"
                value={priceRange.max}
                onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) })}
                className="range-slider"
              />
            </div>
            <div className="filter-group">
              <label>Minimum Rating: {ratingFilter}+ ⭐</label>
              <input
                type="range"
                min="0"
                max="5"
                step="0.5"
                value={ratingFilter}
                onChange={(e) => setRatingFilter(parseFloat(e.target.value))}
                className="range-slider"
              />
            </div>
          </div>
        )}
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="history-panel">
          <h3>📜 Comparison History</h3>
          {comparisonHistory.length === 0 ? (
            <p className="history-empty">No comparison history yet</p>
          ) : (
            <div className="history-list">
              {comparisonHistory.map((item) => (
                <div key={item.id} className="history-item">
                  <div className="history-products">{item.products.join(' vs ')}</div>
                  <div className="history-meta">
                    <span className="history-winner">Winner: {item.recommendation}</span>
                    <span className="history-time">
                      {new Date(item.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Product Selection Grid */}
      <div className="product-selection-grid">
        {getFilteredProducts().map((product) => (
          <div
            key={product.id}
            className={`selectable-product ${selectedProducts.find(p => p.id === product.id) ? 'selected' : ''}`}
            onClick={() => handleProductSelect(product)}
          >
            <div className="selection-checkbox">
              {selectedProducts.find(p => p.id === product.id) && '✓'}
            </div>

            {/* ✅ NEW: Favorite Button */}
            <button
              className="favorite-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleFavorite(product.id);
              }}
            >
              {favoriteProducts.includes(product.id) ? '❤️' : '🤍'}
            </button>

            {renderProductMedia(product)}

            <div className="product-info">
              <h4>{product.name}</h4>
              <div className="product-price">${product.price}</div>
              <div className="product-rating">
                ⭐ <span>{product.rating || 4.0}</span>
              </div>
              <span className="product-category">{product.category}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Selected Bar */}
      {selectedProducts.length > 0 && (
        <div className="selected-bar">
          <div className="selected-count">
            Selected: {selectedProducts.length}/3 products
          </div>
          <div className="selected-actions">
            <button
              className="clear-btn"
              onClick={() => {
                setSelectedProducts([]);
                setComparisonResult(null);
              }}
            >
              Clear All
            </button>
            <button
              className="compare-btn"
              onClick={generateAIComparison}
              disabled={selectedProducts.length < 2}
            >
              🤖 Compare with AI
            </button>
          </div>
        </div>
      )}

      {/* Comparison Results */}
      {comparisonResult && (
        <div className="comparison-results">
          {/* ✅ NEW: Action Buttons */}
          <div className="result-actions">
            <button className="export-btn" onClick={exportToPDF}>
              📥 Export
            </button>
            <button className="export-btn" onClick={handleShare}>
              📤 Share
            </button>
          </div>

          {/* AI Recommendation */}
          <div className="ai-recommendation">
            <div className="recommendation-header">
              <div className="trophy-icon">🏆</div>
              <h3>AI Recommendation</h3>
            </div>
            <div className="recommendation-content">
              <div className="recommended-product">
                {renderProductMedia(comparisonResult.recommendation.product, '120px')}
                <div className="recommended-info">
                  <h4>{comparisonResult.recommendation.product.name}</h4>
                  <div className="recommendation-price">
                    ${comparisonResult.recommendation.product.price}
                  </div>
                  <div className="recommendation-reasons">
                    {comparisonResult.recommendation.reasons.map((reason, index) => (
                      <span key={index} className="reason-badge">✓ {reason}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ✅ NEW: Price Comparison Table */}
          {comparisonResult.priceComparison && (
            <div className="price-comparison-section">
              <h3>💰 Price Analysis</h3>
              <div className="price-table">
                {comparisonResult.priceComparison.map((item, index) => (
                  <div key={index} className={`price-row ${item.status}`}>
                    <span className="product-name">{item.product}</span>
                    <span className="price">${item.price}</span>
                    <span className="vs-average">{item.vsAverage > 0 ? '+' : ''}{item.vsAverage}%</span>
                    {item.savings !== '0.00' && (
                      <span className="savings">Save ${item.savings}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ✅ NEW: Pros & Cons */}
          {comparisonResult.prosCons && (
            <div className="pros-cons-section">
              <h3>⚖️ Pros & Cons</h3>
              <div className="pros-cons-grid">
                {comparisonResult.prosCons.map((item, index) => (
                  <div key={index} className="pros-cons-card">
                    <h4>{item.product}</h4>
                    <div className="pros">
                      <strong>✅ Pros:</strong>
                      <ul>
                        {item.pros.map((pro, i) => (
                          <li key={i}>{pro}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="cons">
                      <strong>❌ Cons:</strong>
                      <ul>
                        {item.cons.map((con, i) => (
                          <li key={i}>{con}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ✅ NEW: Cost of Ownership */}
          {comparisonResult.costOfOwnership && (
            <div className="cost-ownership-section">
              <h3>💵 Total Cost of Ownership</h3>
              <div className="cost-table">
                {comparisonResult.costOfOwnership.map((item, index) => (
                  <div key={index} className="cost-row">
                    <div className="cost-product">{item.product}</div>
                    <div className="cost-details">
                      <span>Initial: ${item.initialCost}</span>
                      <span>Year 1: ${item.totalYear1}</span>
                      <span>Year 3: ${item.totalYear3}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Insights */}
          <div className="ai-insights">
            <h3>🧠 AI Insights</h3>
            <div className="insights-grid">
              {comparisonResult.insights.map((insight, index) => (
                <div key={index} className={`insight-card ${insight.highlight}`}>
                  <div className="insight-icon">{insight.icon}</div>
                  <div className="insight-content">
                    <h4>{insight.title}</h4>
                    <p>{insight.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Scores */}
          <div className="detailed-scores">
            <h3>📊 Detailed Scores</h3>
            <div className="scores-table">
              {comparisonResult.scores.map((item, index) => (
                <div key={index} className="score-row">
                  <div className="score-product">
                    {renderProductMedia(item.product, '60px')}
                    <span>{item.product.name}</span>
                  </div>
                  <div className="score-metrics">
                    <div className="metric">
                      <span className="metric-label">Value</span>
                      <div className="metric-bar">
                        <div className="metric-fill value" style={{ width: `${item.valueScore}%` }}></div>
                      </div>
                      <span className="metric-value">{item.valueScore}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Popularity</span>
                      <div className="metric-bar">
                        <div className="metric-fill popularity" style={{ width: `${item.popularityScore}%` }}></div>
                      </div>
                      <span className="metric-value">{item.popularityScore}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Overall</span>
                      <div className="metric-bar">
                        <div className="metric-fill overall" style={{ width: `${item.overallScore}%` }}></div>
                      </div>
                      <span className="metric-value">{item.overallScore}</span>
                    </div>
                    {/* ✅ NEW METRICS */}
                    <div className="metric">
                      <span className="metric-label">Durability</span>
                      <div className="metric-bar">
                        <div className="metric-fill durability" style={{ width: `${item.durabilityScore}%` }}></div>
                      </div>
                      <span className="metric-value">{item.durabilityScore}</span>
                    </div>
                    <div className="metric">
                      <span className="metric-label">Eco Score</span>
                      <div className="metric-bar">
                        <div className="metric-fill eco" style={{ width: `${item.ecoScore}%` }}></div>
                      </div>
                      <span className="metric-value">{item.ecoScore}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ✅ NEW: Market Trends */}
          {comparisonResult.marketTrends && (
            <div className="market-trends-section">
              <h3>📈 Market Trends</h3>
              <div className="trends-card">
                <div className="trend-item">
                  <strong>Market Segment:</strong> {comparisonResult.marketTrends.marketSegment}
                </div>
                <div className="trend-item">
                  <strong>Customer Satisfaction:</strong> {comparisonResult.marketTrends.customerSatisfaction}
                </div>
                <div className="trend-item">
                  <strong>Recommendation:</strong> {comparisonResult.marketTrends.recommendation}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIProductComparison;